/** Maximum number of records submitted in a single import request. */
import * as moment from "moment";
export const MAX_IMPORT_RECORDS = 2000;

export type PerformanceRatingFieldKey =
  | "ContractorName"
  | "Rating"
  | "Reason"
  | "RatedBy"
  | "From"
  | "To";

export type FieldMapping = Partial<Record<PerformanceRatingFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const PERFORMANCE_RATING_FIELDS: Array<{
  key: PerformanceRatingFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "ContractorName", label: "Contractor", required: true },
  { key: "Rating", label: "Performance Rating Type", required: true },
  { key: "Reason", label: "Reason", required: true },
  { key: "RatedBy", label: "Rated By (Agency)", required: true },
  { key: "From", label: "From (Date)", required: true },
  { key: "To", label: "To (Date)" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of PERFORMANCE_RATING_FIELDS) {
    const normField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping
) {
  const get = (key: PerformanceRatingFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    contractor: get("ContractorName"),
    performanceRatingType: get("Rating"),
    description: get("Reason"),
    ratedBy: get("RatedBy"),
    from:
      (get("From") &&
        moment(get("From"), "DD/MM/YYYY")
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
    to:
      (get("To") &&
        moment(get("To"), "DD/MM/YYYY")
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
  };
}
